import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { LocationProvider } from "@/lib/locations";
import { PrivateRoute } from "@/components/PrivateRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import GoodsIntake from "@/pages/GoodsIntake";
import GoodsOuttake from "@/pages/GoodsOuttake";
import ProductionOrders from "@/pages/ProductionOrders";
import BOM from "@/pages/BOM";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import BarcodeScanner from "@/pages/BarcodeScanner";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Purchases from "@/pages/Purchases";
import Transfers from "@/pages/Transfers";
import QualityControl from "@/pages/QualityControl";
import Analytics from "@/pages/Analytics";
import LotReport from "@/pages/LotReport";
import Locations from "@/pages/Locations";
import Suppliers from "@/pages/Suppliers";
import Tanks from "@/pages/Tanks";
import InventoryImport from "@/pages/InventoryImport";
import SupplierActivityReport from "@/pages/SupplierActivityReport";
import ProductionCalendar from "@/pages/ProductionCalendar";
import { InstallPrompt } from "@/components/InstallPrompt";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-dvh flex items-center justify-center"><div className="operon-spinner" /></div>;
  return user ? <Navigate to="/app/dashboard" replace /> : <Landing />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <LocationProvider>
            <div className="App">
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />

                <Route element={<PrivateRoute />}>
                  <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="/app/dashboard" element={<Dashboard />} />
                  <Route path="/app/inventory" element={<Inventory />} />
                  <Route path="/app/intake" element={<GoodsIntake />} />
                  <Route path="/app/outtake" element={<GoodsOuttake />} />
                  <Route path="/app/production" element={<ProductionOrders />} />
                  <Route path="/app/production-calendar" element={<ProductionCalendar />} />
                  <Route path="/app/bom" element={<BOM />} />
                  <Route path="/app/purchases" element={<Purchases />} />
                  <Route path="/app/transfers" element={<Transfers />} />
                  <Route path="/app/qc" element={<QualityControl />} />
                  <Route path="/app/analytics" element={<Analytics />} />
                  <Route path="/app/lots" element={<LotReport />} />
                  <Route path="/app/locations" element={<Locations />} />
                  <Route path="/app/suppliers" element={<Suppliers />} />
                  <Route path="/app/tanks" element={<Tanks />} />
                  <Route path="/app/inventory/import" element={<InventoryImport />} />
                  <Route path="/app/reports" element={<Reports />} />
                  <Route path="/app/reports/supplier-activity" element={<SupplierActivityReport />} />
                  <Route path="/app/settings" element={<Settings />} />
                  <Route path="/app/billing" element={<Billing />} />
                  <Route path="/billing/success" element={<BillingSuccess />} />
                  <Route path="/app/scanner" element={<BarcodeScanner />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster richColors closeButton position="top-right" />
              <InstallPrompt />
            </div>
          </LocationProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
